import { format } from 'd3-format';
import * as React from 'react';
import {
	discontinuousTimeScaleProviderBuilder,
	Chart,
	ChartCanvas,
	XAxis,
	YAxis,
	AreaSeries,
	MouseCoordinateY,
	SvgPathAnnotation,
	ZoomButtons,
	OHLCTooltip,
	CrossHairCursor,
	lastVisibleItemBasedZoomAnchor,
	MouseCoordinateX,
	Annotate,
	LabelAnnotation,
	Label,
} from 'react-financial-charts';
import { useEffect, useState } from 'react';
import { timeFormat } from 'd3-time-format';

const ScaleProvider = discontinuousTimeScaleProviderBuilder().inputDateAccessor((d) => {
	const estDateString = new Date(d.timestamp).toLocaleString('en-US', { timeZone: 'America/New_York' });
	// Create a new Date object from the adjusted string
	return new Date(estDateString);
});

const StockForecastChart = (props) => {
	let { initialData, ticker } = props;

	if (!initialData || initialData.length === 0) {
		return;
	}
	const [state, setState] = useState(1);

	const margin = { left: 0, right: 48, top: 45, bottom: 24 };

	const [graphData, setGraphData] = useState([]);
	const maxWidth = 1300;
	const width =
		window.innerWidth <= 1023
			? (window.innerWidth < maxWidth ? window.innerWidth : maxWidth) - margin.left - margin.right
			: (window.innerWidth / 2 < maxWidth ? window.innerWidth / 2 : maxWidth) - margin.left - margin.right;
	let height = Math.floor(width * 0.55) - margin.top - margin.bottom;

	if (height < 350) {
		height = 350;
	}

	useEffect(() => {
		setGraphData(sanitizeData(initialData, 'close'));

		window.addEventListener('resize', () => {
			setState((state) => state + 1);
		});
	}, []);

	const calculateAverage = (number1, number2) => {
		return (Number(number1) + Number(number2)) / 2;
	};

	const theme = HSThemeAppearance.getAppearance();

	const dateTimeFormat = timeFormat('%d %b %Y %H:%M');
	const timeDisplayFormat = timeFormat(dateTimeFormat);

	const { data, xScale, xAccessor, displayXAccessor } = ScaleProvider(graphData);

	let calculatedData = data;

	const pricesDisplayFormat = format('.2f');
	const max = xAccessor(data[data.length - 1]);
	const min = xAccessor(data[Math.max(0, data.length - 100)]);
	const xExtents = [min, max + 5];

	const gridHeight = height - margin.top - margin.bottom;

	const chartHeight = gridHeight;

	const candleChartExtents = (data) => {
		return [data.high, data.low];
	};

	const whenBuy = (data) => {
		return data.signal === 2;
	};

	const whenSell = (data) => {
		return data.signal === 0;
	};

	const labelBuyAnnotation = {
		text: 'BUY',
		fill: '#84cc16',
		fontWeight: 'bold',
		fontSize: '16',
		tooltip: 'Buy',
		y: ({ yScale, datum }: any) => yScale(datum.close) - 25,
	};

	const labelSellAnnotation = {
		text: 'SELL',
		fill: '#ef4444',
		fontWeight: 'bold',
		fontSize: '16',
		tooltip: 'Sell',
		y: ({ yScale, datum }: any) => yScale(datum.close) - 25,
	};

	const svgAnnotationBuy = {
		fill: '#84cc16',
		path: () =>
			'M3.5 2.75a.75.75 0 0 0-1.5 0v14.5a.75.75 0 0 0 1.5 0v-4.392l1.657-.348a6.449 6.449 0 0 1 4.271.572 7.948 7.948 0 0 0 5.965.524l2.078-.64A.75.75 0 0 0 18 12.25v-8.5a.75.75 0 0 0-.904-.734l-2.38.501a7.25 7.25 0 0 1-4.186-.363l-.502-.2a8.75 8.75 0 0 0-5.053-.439l-1.475.31V2.75Z',
		pathWidth: 12,
		pathHeight: 22,
		tooltip: 'Buy',
		y: ({ yScale, datum }: any) => yScale(datum.close),
	};

	const svgAnnotationSell = {
		fill: '#ef4444',
		path: () =>
			'M3.5 2.75a.75.75 0 0 0-1.5 0v14.5a.75.75 0 0 0 1.5 0v-4.392l1.657-.348a6.449 6.449 0 0 1 4.271.572 7.948 7.948 0 0 0 5.965.524l2.078-.64A.75.75 0 0 0 18 12.25v-8.5a.75.75 0 0 0-.904-.734l-2.38.501a7.25 7.25 0 0 1-4.186-.363l-.502-.2a8.75 8.75 0 0 0-5.053-.439l-1.475.31V2.75Z',
		pathWidth: 12,
		pathHeight: 22,
		tooltip: 'Sell',
		y: ({ yScale, datum }: any) => yScale(datum.close),
	};

	const sanitizeData = (array, key) => {
		// Initialize with Infinity so any value will be lower
		// Find the lowest value
		let lowestValue = Infinity;
		array.forEach((item) => {
			if (item.hasOwnProperty(key) && item[key] < lowestValue) {
				lowestValue = item[key];
			}
		});

		// Filter out items with lowestValue zero
		return array.filter((item) => !(item.hasOwnProperty(key) && item[key] === lowestValue && lowestValue === 0));
	};

	return (
		<div>
			<ChartCanvas
				className={'finquota-chart col-span-10 mx-auto'}
				height={height}
				ratio={3}
				width={width}
				margin={margin}
				data={calculatedData}
				disableZoom={false}
				displayXAccessor={displayXAccessor}
				seriesName="Data"
				xScale={xScale}
				xAccessor={xAccessor}
				zoomAnchor={lastVisibleItemBasedZoomAnchor}
				xExtents={xExtents}
			>
				<Chart id={3} height={chartHeight} yExtents={candleChartExtents}>
					<XAxis
						domainClassName="finquota-charts-axis-domain"
						zoomCursorClassName="finquota-charts-ew-resize-cursor"
						className="finquota-charts-x-axis"
						showGridLines
						gridLinesStrokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
						strokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
						tickLabelFill={theme === 'dark' ? '#64748b' : '#374151'}
						tickStrokeStyle={theme === 'dark' ? '#64748b' : '#374151'}
					/>
					<YAxis
						className="finquota-charts-y-axis"
						showGridLines
						zoomCursorClassName="finquota-charts-ns-resize-cursor"
						gridLinesStrokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
						strokeStyle={theme === 'dark' ? '#64748b' : '#9ca3af'}
						tickLabelFill={theme === 'dark' ? '#64748b' : '#374151'}
						tickStrokeStyle={theme === 'dark' ? '#64748b' : '#374151'}
						tickFormat={pricesDisplayFormat}
					/>
					{width > 700 && <ZoomButtons />}
					<MouseCoordinateX displayFormat={timeDisplayFormat} />
					<MouseCoordinateY rectWidth={margin.right} displayFormat={pricesDisplayFormat} />

					<AreaSeries yAccessor={(d) => d.close} />
					<MouseCoordinateY rectWidth={margin.right} displayFormat={pricesDisplayFormat} />
					<ZoomButtons />
					<OHLCTooltip
						fontWeight={500}
						labelFontWeight={500}
						className="finquota-charts-tooltip-hover"
						labelFill={theme === 'dark' ? '#FFFFFF' : '#000000'}
						textFill={theme === 'dark' ? '#FFFFFF' : '#000000'}
						ohlcFormat={format('$,.2f')}
						origin={[0, -35]}
					/>
					<Label
						text={ticker || ''}
						fillStyle={theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}
						x={(width - margin.left - margin.right) / 2}
						y={(height - margin.top - margin.bottom) / 2}
					/>
					{svgAnnotationBuy && <Annotate className={'-z-10'} with={LabelAnnotation} usingProps={labelBuyAnnotation} when={whenBuy} />}
					{svgAnnotationBuy && <Annotate className={'-z-10'} with={SvgPathAnnotation} usingProps={svgAnnotationBuy} when={whenBuy} />}

					{svgAnnotationSell && <Annotate className={'-z-10'} with={SvgPathAnnotation} usingProps={svgAnnotationSell} when={whenSell} />}
					{svgAnnotationSell && <Annotate className={'-z-10'} with={LabelAnnotation} usingProps={labelSellAnnotation} when={whenSell} />}
				</Chart>
				<CrossHairCursor />
			</ChartCanvas>
		</div>
	);
};

export default StockForecastChart;
